import { Dialog } from "@headlessui/react";
import { IconMail, IconX } from "@tabler/icons-react";
import { Controller, useForm } from "react-hook-form";
import { useState } from "react";
import Button from "./Button";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import { sendEmail } from "../store/claim/actions";

interface ClaimSendEmailModalProps {
  isOpen: boolean;
  onClose: () => void;
  customerEmail: string;
}

function ClaimSendEmailModal({
  isOpen,
  onClose,
  customerEmail,
}: ClaimSendEmailModalProps) {
  const [submitted, setSubmitted] = useState(false);

  const {
    control,
    handleSubmit,
    watch,
    formState: { isValid },
    reset,
  } = useForm({
    mode: "onChange",
    defaultValues: {
      email: customerEmail || "",
      confirmEmail: false,
    },
  });
  const emailValue = watch("email");
  const emailRegex = /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  const emailValid = emailRegex.test(emailValue);

  const confirmChecked = watch("confirmEmail");
  const dispatch = useDispatch();
  const onSubmit = (data: any) => {
    setSubmitted(true);
    dispatch(
      sendEmail(
        {
          data: {
            toEmail: [data?.email],
          },
        },
        () => {
          onClose();
          setSubmitted(false);
          toast.success("Email has been send");
          reset({
            email: "",
            confirmEmail: false,
          });
        }
      )
    );
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="fixed inset-0 z-50">
      <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4 sm:p-6">
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="send-email-title"
          className="w-full max-w-md overflow-hidden rounded-xl bg-white shadow-xl flex flex-col"
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b border-gray-200 gap-3 px-4 py-3 text-gray-800">
            <h2
              id="send-email-title"
              className="text-base sm:text-lg font-medium flex items-center gap-2"
            >
              <IconMail size={20} className="text-slate-500" />
              Send Email
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <IconX />
            </button>
          </div>

          {/* Form Body */}
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="flex flex-col gap-4 p-4 sm:p-6 flex-1 overflow-y-auto text-sm text-gray-700"
          >
            {/* Email Field (Read-only) */}
            <div>
              <label className="block mb-1 font-medium">Customer Email</label>
              <Controller
                control={control}
                name="email"
                rules={{
                  required: "Email is required",
                  pattern: {
                    value: /^[a-zA-Z0-9.+_-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
                    message: "Please enter a valid email",
                  },
                }}
                render={({ field, fieldState: { error } }) => (
                  <>
                    <input
                      {...field}
                      type="email"
                      className={`w-full appearance-none border read-only:bg-gray-100 border-gray-300 px-3 py-2.5 rounded-lg disabled:bg-gray-50 disabled:border-gray-200 text-gray-900 placeholder-gray-500 invalid:border-1 invalid:border-red-500 focus:outline-none focus:shadow-none focus:ring-0 text-sm ${
                        error
                          ? "border-red-500 focus:border-red-500"
                          : watch("confirmEmail")
                            ? "border-gray-200 bg-gray-100 text-gray-500 cursor-not-allowed"
                            : "border-gray-300"
                      }`}
                      readOnly={watch("confirmEmail")}
                    />
                    {error && (
                      <p className="mt-1 text-red-500 text-xs">
                        {error.message}
                      </p>
                    )}
                  </>
                )}
              />
            </div>

            {/* Checkbox */}
            <div className="flex items-center gap-2">
              <Controller
                control={control}
                name="confirmEmail"
                rules={{ required: true }}
                render={({ field }) => (
                  <input
                    type="checkbox"
                    id="confirmEmail"
                    className="accent-blue-500"
                    checked={field.value}
                    disabled={!emailValid}
                    onChange={(e) => field.onChange(e.target.checked)}
                    onBlur={field.onBlur}
                    name={field.name}
                    ref={field.ref}
                  />
                )}
              />
              <label htmlFor="confirmEmail" className="text-sm">
                I have confirmed the customer’s email.
              </label>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-2 border-t border-gray-200 pt-4 mt-auto">
              <Button variant="blue" type="button" onClick={onClose}>
                Cancel
              </Button>
              <Button
                variant="blue"
                type="submit"
                disabled={!isValid || !confirmChecked || submitted}
              >
                {submitted ? "Sending..." : "Send Email"}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Dialog>
  );
}

export default ClaimSendEmailModal;
